import { z } from "zod";

import {
  COGNITO_ADMIN_GROUP,
  COGNITO_AUTOMATION_GROUP,
  COGNITO_INTERNAL_STAFF_GROUPS,
} from "@recobra/core";

import { MutationActorSchema } from "../automation-signals";
import { CaseWorkflowStep } from "../case-core";
import { RequestedDocumentSchema } from "../document";
import { CompletionEffectsSchema } from "./completion-effects";
import { DvcSearchDetailsSchema } from "./dvc-search";
import { TaskEmailSchema } from "./emails";
import { TaskHistoryEntrySchema } from "./history";
import { LiabilityDeclineReasonSchema, LiabilityReviewSnapshotSchema } from "./liability-decline";
import { TaskReminderSchema } from "./reminders";
import { RequirementProgressSchema } from "./requirements";
import { TASK_DESCRIPTION_MAX, TaskContextType, TaskPriority, TaskStatus } from "./schemas";
import { SystemTaskKeySchema } from "./system-task-key";

const internalStaff = COGNITO_INTERNAL_STAFF_GROUPS;
const schedulingManagers = [COGNITO_ADMIN_GROUP, "Manager"];

const PreviousTaskActivitySchema = z.object({
  taskId: z.uuid().describe("The activity that was resolved"),
  type: z.string().min(1).describe("That activity's task type"),
  solution: z.string().min(1).optional().describe("The resolution it was completed with"),
  notes: z
    .string()
    .min(1)
    .optional()
    .describe("What was written when it was resolved; absent when it was resolved without a note"),
});
export type PreviousTaskActivity = z.infer<typeof PreviousTaskActivitySchema>;

export const TaskAiAnalysisSchema = z.object({
  status: z.enum(["pending", "completed", "failed"]),
  runId: z.uuid(),
  suggestedSolution: z.string().min(1).optional(),
  reasoning: z.string().optional(),
  updatedAt: z.iso.datetime(),
});
export type TaskAiAnalysis = z.infer<typeof TaskAiAnalysisSchema>;

export const TaskSchema = z.object({
  id: z.uuid().describe("Unique identifier of the task").meta({
    visibleTo: "*",
  }),
  type: z.string().min(1).describe("Its task type's id").meta({ visibleTo: "*" }),
  system: SystemTaskKeySchema.nullable()
    .describe("Its task type's code-owned behavior, copied at creation")
    .meta({ visibleTo: "*" }),
  description: z
    .string()
    .max(TASK_DESCRIPTION_MAX)
    .nullish()
    .optional()
    .describe("Detailed description of what needs to be done")
    .meta({ visibleTo: "*" }),
  priority: TaskPriority.nullish()
    .describe("Optional urgency rating")
    .meta({ visibleTo: internalStaff }),
  firstAssignedAt: z.iso.datetime().nullish().meta({ visibleTo: schedulingManagers }),
  deadlineDate: z.iso.date().nullish().meta({ visibleTo: "*" }),
  reminders: z.array(TaskReminderSchema).default([]).meta({ visibleTo: "*" }),
  emails: z
    .array(TaskEmailSchema)
    .default([])
    .describe("Emails sent to the task's assignees, oldest first")
    .meta({ visibleTo: internalStaff }),
  assignedTo: z
    .string()
    .nullish()
    .describe("Unique identifier of the assigned user")
    .meta({ visibleTo: "*" }),
  contextType: TaskContextType.describe(
    "The type of parent object this task is associated with",
  ).meta({ visibleTo: "*" }),
  contextId: z
    .uuid()
    .describe("The unique identifier of the parent object")
    .meta({ visibleTo: "*" }),
  contextCaseNumber: z.number().int().min(1000).nullable().meta({ visibleTo: "*" }),
  contextStage: z
    .string()
    .min(1)
    .describe("The stage of the parent object when this task was created")
    .meta({ visibleTo: "*" }),
  movesCaseTo: CaseWorkflowStep.optional()
    .describe("Step creating this task moves its case to, from its flow's stage")
    .meta({ visibleTo: "*" }),
  status: TaskStatus.describe("The current lifecycle status of the task").meta({
    visibleTo: "*",
  }),
  waitingStatus: z
    .string()
    .nullish()
    .describe("Why this task is parked; only set while status is waiting")
    .meta({ visibleTo: "*" }),
  completedAt: z.iso
    .datetime()
    .optional()
    .describe("Precise timestamp when the task transitioned to done")
    .meta({ visibleTo: "*" }),
  solution: z
    .string()
    .nullish()
    .describe("The selected solution (recorded when status transitions to done)")
    .meta({ visibleTo: "*" }),
  completionEffects: CompletionEffectsSchema.optional().meta({ visibleTo: "*" }),
  notes: z
    .string()
    .optional()
    .describe("Latest task execution notes, saved before or at completion")
    .meta({ visibleTo: internalStaff }),
  declineReason: LiabilityDeclineReasonSchema.optional()
    .describe("Structured reason selected when the liability analysis was declined")
    .meta({ visibleTo: internalStaff }),
  liabilityReviewSnapshot: LiabilityReviewSnapshotSchema.optional()
    .describe("Reviewer-confirmed liability values at task completion")
    .meta({ visibleTo: internalStaff }),
  aiAnalysis: TaskAiAnalysisSchema.optional().meta({ visibleTo: internalStaff }),
  requirementProgress: RequirementProgressSchema.default({ satisfied: {}, waived: {} })
    .describe("Checklist answers stamped where they were proven")
    .meta({ visibleTo: "*" }),
  requestedDocuments: z
    .array(RequestedDocumentSchema)
    .default([])
    .describe("Documents this task asks for; each becomes a checklist row")
    .meta({ visibleTo: "*" }),
  dvcSearchDetails: DvcSearchDetailsSchema.optional()
    .describe("The DVC search type and value this task asks to look up")
    .meta({ visibleTo: "*" }),
  targetCaseStep: CaseWorkflowStep.optional()
    .describe("Case step this document gate is intended to unblock")
    .meta({ visibleTo: "*" }),
  previousActivities: z
    .array(PreviousTaskActivitySchema)
    .optional()
    .describe(
      "The last activities closed on this task's parent before it was created, oldest first, " +
        "each with whatever was written when it was resolved. Absent when the caller lacks " +
        "internalStaff visibility, and only computed on the single-task read.",
    )
    .meta({ visibleTo: internalStaff }),
  history: z
    .array(TaskHistoryEntrySchema)
    .optional()
    .describe(
      "Sequential list of audit history entries for state updates. Absent when the caller " +
        "lacks internalStaff visibility, not merely empty — always present on the stored task.",
    )
    .meta({ visibleTo: internalStaff }),
  createdBy: MutationActorSchema.describe("Who originally created this task").meta({
    visibleTo: [...internalStaff, COGNITO_AUTOMATION_GROUP],
  }),
  sourceTaskId: z
    .uuid()
    .nullish()
    .meta({ visibleTo: [...internalStaff, COGNITO_AUTOMATION_GROUP] }),
  createdAt: z.iso.datetime().describe("The creation timestamp").meta({
    visibleTo: "*",
  }),
  updatedAt: z.iso.datetime().describe("The last updated timestamp").meta({
    visibleTo: "*",
  }),
});
export type Task = z.infer<typeof TaskSchema>;
